import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { proactiviteeEventViewerPageQuery, proactiviteeEventViewerTableQuery, type EventViewerRow } from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Remote Event Viewer - remote_event_viewer.php: Live / Archived,
 * RemoteEventsModel (Source / Category / Event ID / Count / Last seen).
 * Arhivarea (scriere) nu se muta. AI column nu se muta.
 */

const TABLE = 'remote-events-table'

export function DeviceProactiviteeEventViewer({ id, open }: ScreenProps) {
  const page = useQuery(proactiviteeEventViewerPageQuery(id))
  const [view, setView] = useState<'live' | 'archived'>('live')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 4, dir: 'desc' })
  const [search] = useState('')
  const [openRows, setOpenRows] = useState<string[]>([])

  const table = useQuery({
    ...proactiviteeEventViewerTableQuery(id, {
      start,
      length,
      orderColumn: order.column,
      orderDir: order.dir,
      search,
      view,
    }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const archivedCount = table.data?.archivedCount ?? page.data.archivedCount
  const empty = table.data && table.data.data.length === 0 ? page.data.empty : null

  return (
    <div className="row">
      <div className="col-md-12">
        <p className="text-muted" style={{ margin: '0 0 12px', fontSize: 12 }}>
          Errors from the machine itself: Event Viewer on Windows, the system journal on Linux.
          Identical lines are grouped; Count is how many times that error repeated.
          Failures of the OptimumDesk agent are{' '}
          <a
            href="javascript:void(0)"
            className="js-open-agent-log"
            onClick={(e) => {
              e.preventDefault()
              open('overview/agentSettings/')
            }}
          >
            Agent Log
          </a>
          .
        </p>
        <div className="od-ev-views">
          <button
            type="button"
            className={`btn btn-default btn-xs js-events-view${view === 'live' ? ' is-on' : ''}`}
            data-view="live"
            onClick={() => {
              setView('live')
              setStart(0)
            }}
          >
            Live
          </button>
          <button
            type="button"
            className={`btn btn-default btn-xs js-events-view${view === 'archived' ? ' is-on' : ''}`}
            data-view="archived"
            onClick={() => {
              setView('archived')
              setStart(0)
            }}
          >
            Archived <span className="od-ev-count">{archivedCount}</span>
          </button>
          <span className="od-ev-hint">
            Archiving puts an error aside without deleting anything &mdash; and it comes back on its own if the error happens
            again.
          </span>
        </div>
        <style>{`
.od-ev-views { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin: 0 0 10px; }
.od-ev-views .js-events-view.is-on {
    background: var(--od-ink, #1f2933); border-color: var(--od-ink, #1f2933); color: #fff;
}
.od-ev-count {
    display: inline-block; min-width: 16px; margin-left: 4px; padding: 0 5px;
    border-radius: 8px; background: rgba(0, 0, 0, .12);
    font-size: 10px; font-weight: 700; line-height: 15px;
}
.od-ev-views .js-events-view.is-on .od-ev-count { background: rgba(255, 255, 255, .25); }
.od-ev-hint {
    flex: 1 1 260px; min-width: 0; font-size: 11px; line-height: 1.4;
    color: var(--od-ink-faint, #6b7480);
}
`}</style>
        <ViewerTable
          rows={table.data?.data ?? []}
          total={table.data?.recordsFiltered ?? 0}
          fetching={table.isFetching}
          error={table.isError ? (table.error as Error).message : null}
          empty={empty}
          start={start}
          length={length}
          order={order}
          openRows={openRows}
          onStart={setStart}
          onLength={(n) => {
            setLength(n)
            setStart(0)
          }}
          onOrder={(next) => {
            setOrder(next)
            setStart(0)
          }}
          onExpand={(rowId) => setOpenRows((cur) => (cur.includes(rowId) ? cur.filter((r) => r !== rowId) : [...cur, rowId]))}
        />
      </div>
    </div>
  )
}

const COLUMNS: { key: keyof EventViewerRow; title: string; orderable: boolean; width?: number; center?: boolean }[] = [
  { key: 'source', title: 'Source', orderable: true },
  { key: 'category', title: 'Category', orderable: true, width: 110 },
  { key: 'log_id', title: 'Event ID', orderable: true, width: 90 },
  { key: 'count', title: 'Count', orderable: true, width: 70, center: true },
  { key: 'last_seen', title: 'Last seen', orderable: true, width: 180 },
]

function ViewerTable({
  rows,
  total,
  fetching,
  error,
  empty,
  start,
  length,
  order,
  openRows,
  onStart,
  onLength,
  onOrder,
  onExpand,
}: {
  rows: EventViewerRow[]
  total: number
  fetching: boolean
  error: string | null
  empty: string | null
  start: number
  length: number
  order: { column: number; dir: 'asc' | 'desc' }
  openRows: string[]
  onStart(n: number): void
  onLength(n: number): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
  onExpand(rowId: string): void
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className="table table-striped table-hover dataTable remote-events-table no-footer" width="100%" id={TABLE} role="grid">
            <thead>
              <tr role="row">
                {COLUMNS.map((col, i) => {
                  const sorted = order.column === i
                  const cls = [
                    col.center ? 'td-small a-center' : 'td-small a-left',
                    col.orderable ? (sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting') : 'sorting_disabled',
                  ].join(' ')
                  return (
                    <th
                      key={col.key}
                      className={cls}
                      style={col.width ? { width: col.width } : undefined}
                      onClick={() => {
                        if (!col.orderable) return
                        onOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })
                      }}
                    >
                      {col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={5} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={5} className="dataTables_empty">
                    {empty ?? (fetching ? '' : 'No data available in table')}
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        className={col.center ? 'td-small a-center' : 'td-small a-left'}
                        dangerouslySetInnerHTML={{
                          __html:
                            col.key === 'source' && openRows.includes(row.DT_RowId)
                              ? expandedSource(row)
                              : row[col.key],
                        }}
                        onClick={(e) => {
                          const t = e.target as HTMLElement
                          if (t.closest('.js-expand-event')) {
                            e.preventDefault()
                            onExpand(row.DT_RowId)
                          }
                        }}
                      />
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}

function expandedSource(row: EventViewerRow): string {
  const title = row.source.match(/<strong>[\s\S]*?<\/strong>/)?.[0] ?? ''
  const body = escapeHtml(row.message)
  return (
    title +
    '<div class="color-light-grey font-10" style="white-space:normal;word-break:break-word;margin-top:2px">' +
    body +
    '</div>'
  )
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}
